import React, { useState, useEffect, useMemo } from 'react';
import { GalleryImage } from '../types/gallery';
import { galleryService } from '../services/galleryService';
import { colors } from '../utils/constants';

const GalleryPage: React.FC = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [categories, setCategories] = useState<string[]>(['All']);

  useEffect(() => {
    console.log('[GalleryPage] Component mounted, initializing gallery service');

    const applyGalleryImages = (galleryImages: GalleryImage[], source: string) => {
      console.log(`[GalleryPage] Applying gallery images (${source}):`, {
        count: galleryImages.length,
        images: galleryImages
      });

      setImages(galleryImages);
      setLoading(false);

      const cats = galleryImages
        .map((img) => img.category)
        .filter((cat): cat is string => cat !== undefined && cat.trim() !== '');

      const uniqueCategories = Array.from(new Set(cats));
      uniqueCategories.sort();
      setCategories(['All', ...uniqueCategories]);
    };

    const unsubscribe = galleryService.subscribeToGalleryImages((galleryImages) => {
      applyGalleryImages(galleryImages, 'subscription');
    });

    // One-shot fetch also clears loading. The RTDB onValue listener may never fire
    // (e.g. websocket issues) while get() or local JSON still works — without this,
    // the page can spin forever.
    galleryService
      .getGalleryImages()
      .then((galleryImages) => applyGalleryImages(galleryImages, 'initial fetch'))
      .catch((err) => {
        console.error('[GalleryPage] Error loading gallery images:', err);
        setError('Failed to load gallery images');
        setLoading(false);
      });

    return () => {
      console.log('[GalleryPage] Component unmounting, unsubscribing from gallery service');
      unsubscribe();
    };
  }, []);

  const filteredImages = useMemo(() => {
    console.log('[GalleryPage] Filtering images:', {
      totalImages: images.length,
      selectedCategory,
      allImages: images
    });

    if (selectedCategory === 'All') {
      console.log('[GalleryPage] No filter applied, returning all images');
      return images;
    }

    const filtered = images.filter((img) => img.category?.toLowerCase() === selectedCategory.toLowerCase());
    console.log('[GalleryPage] Filtered images:', {
      filteredCount: filtered.length,
      filtered
    });

    return filtered;
  }, [images, selectedCategory]);

  const handleImageClick = (image: GalleryImage) => {
    console.log('[GalleryPage] Image clicked:', image);
    setSelectedImage(image);
  };

  const handleCloseModal = () => {
    console.log('[GalleryPage] Closing modal');
    setSelectedImage(null);
  };

  console.log('[GalleryPage] Render state:', {
    loading,
    error,
    imagesCount: images.length,
    filteredImagesCount: filteredImages.length,
    categoriesCount: categories.length,
    selectedCategory
  });

  if (loading) {
    console.log('[GalleryPage] Rendering loading state');
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading gallery...</p>
        </div>
      </div>
    );
  }

  if (error) {
    console.log('[GalleryPage] Rendering error state:', error);
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <p style={styles.errorText}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Gallery</h1>
        <p style={styles.subtitle}>Browse our collection of trophy designs and custom work</p>
      </div>

      {/* Category Filter */}
      {categories.length > 1 && (
        <div style={styles.filterContainer}>
          <div style={styles.categoryList}>
            {categories.map((category) => (
              <button
                key={category}
                style={{
                  ...styles.categoryChip,
                  ...(selectedCategory === category ? styles.categoryChipSelected : {}),
                }}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Image Grid */}
      {filteredImages.length === 0 ? (
        <div style={styles.emptyContainer}>
          <p style={styles.emptyText}>No images found</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {filteredImages.map((image) => (
            <div
              key={image.id}
              style={styles.imageCard}
              onClick={() => handleImageClick(image)}
            >
              <img
                src={image.thumbnailUrl || image.imageUrl}
                alt={image.title}
                style={styles.image}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      )}

      {/* Image Detail Modal */}
      {selectedImage && (
        <div style={styles.modal} onClick={handleCloseModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button style={styles.closeButton} onClick={handleCloseModal}>
              ×
            </button>
            <img
              src={selectedImage.imageUrl}
              alt={selectedImage.title}
              style={styles.modalImage}
            />
            <div style={styles.modalInfo}>
              <h2 style={styles.modalTitle}>{selectedImage.title}</h2>
              {selectedImage.description && (
                <p style={styles.modalDescription}>{selectedImage.description}</p>
              )}
              {selectedImage.category && (
                <p style={styles.modalCategory}>Category: {selectedImage.category}</p>
              )}
              {selectedImage.tags && selectedImage.tags.length > 0 && (
                <div style={styles.tagsContainer}>
                  {selectedImage.tags.map((tag, index) => (
                    <span key={index} style={styles.tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    width: '100%',
    boxSizing: 'border-box' as const,
    overflowX: 'clip' as const,
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: colors.textDark,
    marginBottom: '10px',
  },
  subtitle: {
    fontSize: '16px',
    color: colors.textGray,
  },
  filterContainer: {
    marginBottom: '30px',
    overflowX: 'auto',
  },
  categoryList: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'nowrap',
    paddingBottom: '10px',
  },
  categoryChip: {
    padding: '10px 24px',
    borderRadius: '20px',
    border: `1px solid ${colors.borderLight}`,
    backgroundColor: colors.backgroundWhite,
    color: colors.textDark,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s',
  },
  categoryChipSelected: {
    backgroundColor: colors.primaryGold,
    borderColor: colors.primaryGold,
    color: colors.backgroundWhite,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
    gap: '20px',
    width: '100%',
  },
  imageCard: {
    position: 'relative',
    aspectRatio: '1',
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: `4px solid ${colors.borderLight}`,
    borderTop: `4px solid ${colors.primaryGold}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '20px',
    fontSize: '16px',
    color: colors.textGray,
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
  },
  errorText: {
    fontSize: '16px',
    color: colors.error,
  },
  emptyContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
  },
  emptyText: {
    fontSize: '16px',
    color: colors.textGray,
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modalContent: {
    position: 'relative',
    maxWidth: '900px',
    maxHeight: '90vh',
    backgroundColor: colors.backgroundWhite,
    borderRadius: '8px',
    overflow: 'auto',
  },
  closeButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'rgba(0, 0, 0, 0.5)',
    border: 'none',
    color: colors.backgroundWhite,
    fontSize: '32px',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  modalImage: {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
  modalInfo: {
    padding: '20px',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: colors.textDark,
    marginBottom: '10px',
  },
  modalDescription: {
    fontSize: '16px',
    color: colors.textGray,
    marginBottom: '10px',
  },
  modalCategory: {
    fontSize: '14px',
    color: colors.textLight,
    marginBottom: '10px',
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '15px',
  },
  tag: {
    padding: '5px 12px',
    backgroundColor: colors.backgroundCard,
    borderRadius: '12px',
    fontSize: '12px',
    color: colors.textDark,
  },
};

export default GalleryPage;
